'use client'

import { useMemo, useRef } from 'react'
import { InView } from 'react-intersection-observer'

import { ViewAllUserItemsLink } from '@marketplace-web/profile/profile-feature'
import { useFetch } from '@marketplace-web/core-api/api-client-util'
import {
  getItemsByName,
  transformItemsBlockItemDtoToProductItem,
} from '@marketplace-web/item-page/item-page-data'
import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'

import ItemViewItems from '../../components/ItemViewItems/ItemViewItems'
import { ItemPageClosetPluginModel } from './types'
import ItemsGridSkeleton from '../../components/ItemsGridSkeleton'

type Props = {
  data: ItemPageClosetPluginModel
}

const ItemPageClosetPlugin = ({ data }: Props) => {
  const { itemId, itemUserId } = data

  const { fetch: fetchClosetItems, data: closetItemsData, isLoading } = useFetch(getItemsByName)
  const hasFetched = useRef(false)
  const trackExpose = useTrackAbTestCallback()

  const linkRemovalTest = useAbTest('user_closet_link_removal_web')
  const linkEnabled = !linkRemovalTest || linkRemovalTest?.variant === 'off'

  const userItems = useMemo(() => {
    return closetItemsData?.items.filter(item => item.id !== itemId) || []
  }, [closetItemsData, itemId])

  const onInViewChange = (inView: boolean) => {
    if (!inView || hasFetched.current) return

    fetchClosetItems({
      itemId: itemId.toString(),
      name: 'other_user_items',
    })
    hasFetched.current = true
  }

  const onLinkViewChange = (inView: boolean) => {
    if (!inView || !linkRemovalTest) return

    trackExpose(linkRemovalTest)
  }

  return (
    <InView as="div" data-testid="item-page-closet-plugin" onChange={onInViewChange}>
      {(!closetItemsData || isLoading) && <ItemsGridSkeleton />}
      {userItems?.length > 0 && (
        <>
          <ItemViewItems
            items={userItems.map(transformItemsBlockItemDtoToProductItem)}
            contentSource="other_user_items"
            itemTestId="owner-item"
          />
          <InView
            as="div"
            data-testid="view-all-user-items-link-container"
            onChange={onLinkViewChange}
            triggerOnce
          >
            {linkEnabled && !!closetItemsData?.pagination.total_entries && (
              <ViewAllUserItemsLink
                ownerItemCount={closetItemsData.pagination.total_entries}
                ownerId={itemUserId}
                itemId={itemId.toString()}
              />
            )}
          </InView>
        </>
      )}
    </InView>
  )
}

export default ItemPageClosetPlugin
